import { useState, useEffect } from "react";
import { toggleSaveRestaurant } from "../../lib/api/restaurant.apis";
import { useAuth } from "../../Context/AuthContext";

interface SaveButtonProps {
  restaurantId: string;
  onToggle?: (saved: boolean) => void;
  variant?: "icon" | "compact" | "full";
  stroke?: string;
  size?: number;
}

export default function SaveButton({
  restaurantId,
  onToggle,
  variant = "icon",
  stroke = "gray",
  size,
}: SaveButtonProps) {
  const { user, setUser } = useAuth();
  const [animating, setAnimating] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Initialize isSaved from backend or localStorage
  useEffect(() => {
    if (user) {
      setIsSaved(user.savedRestaurants?.includes(restaurantId) ?? false);
    } else {
      const guestLikes: string[] = JSON.parse(localStorage.getItem("guestLikes") || "[]");
      setIsSaved(guestLikes.includes(restaurantId));
    }
  }, [restaurantId, user]);

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnimating(true);

    try {
      if (user) {
        // Logged-in user → sync with backend
        const saved = await toggleSaveRestaurant(restaurantId);
        setIsSaved(saved);

        if (setUser) {
          const updatedRestaurants = saved
            ? [...user.savedRestaurants, restaurantId]
            : user.savedRestaurants.filter((id) => id !== restaurantId);
          setUser({ ...user, savedRestaurants: updatedRestaurants });
        }

        onToggle?.(saved);
      } else {
        // Guest → localStorage only
        let guestLikes: string[] = JSON.parse(localStorage.getItem("guestLikes") || "[]");

        if (guestLikes.includes(restaurantId)) {
          guestLikes = guestLikes.filter((id) => id !== restaurantId);
          setIsSaved(false);
        } else {
          guestLikes.push(restaurantId);
          setIsSaved(true);
        }

        localStorage.setItem("guestLikes", JSON.stringify(guestLikes));
        onToggle?.(guestLikes.includes(restaurantId));
      }
    } catch (error) {
      console.error("Save error:", error);
    } finally {
      setTimeout(() => setAnimating(false), 250);
    }
  };

  // Heart icon
  const HeartIcon = ({ size = 6 }: { size?: number }) => (
    <svg
      className={`w-${size} h-${size} transition-all duration-300 ${animating ? "scale-125" : "scale-100"}`}
      fill={isSaved ? "#ff4e1d" : "rgba(0,0,0,0.4)"}
      stroke={isSaved ? "#ff4e1d" : stroke}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  );

  // Render based on variant
  switch (variant) {
    case "icon":
      return (
        <button onClick={handleSave} className="transition-transform duration-200 hover:scale-110">
          <HeartIcon size={size || 8} />
        </button>
      );

    case "compact":
      return (
        <button
          onClick={handleSave}
          className={`flex items-center justify-center hover:bg-gray-100 lg:border border-gray-100 lg:shadow rounded-4xl transition-all duration-200 lg:p-2 lg:px-3 ${
            isSaved ? "lg:bg-[#FFF8E7]" : ""
          }`}
        >
          <HeartIcon size={5} />
          <span className="hidden sm:inline text-sm lg:text-base font-medium text-gray-900">
            {isSaved ? "Saved" : "Save"}
          </span>
        </button>
      );

    case "full":
    default:
      return (
        <button
          onClick={handleSave}
          className={`flex w-full items-center justify-center space-x-2 hover:bg-gray-100 border border-gray-600 border-2 rounded-4xl transition-all duration-200 p-2 ${
            isSaved ? "bg-[#FFF8E7]" : ""
          }`}
        >
          <HeartIcon size={8} />
          <span className="sm:inline text-xl lg:text-base font-medium text-gray-900">
            {isSaved ? "Saved" : "Save"}
          </span>
        </button>
      );
  }
}
